import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, qs } from "@/lib/api";
import { BRANDS } from "@/lib/brands";
import { formatAgo, formatInt } from "@/lib/format";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";

export interface AutomationSummary {
  id: number;
  name: string;
  description: string | null;
  status: "draft" | "shadow" | "active" | "paused" | "archived" | string;
  brand_id: number | null;
  keap_campaign_id: number | null;
  current_version_id: number | null;
  live_count: number;
  total_count: number;
  updated_at: string;
}

export const AUTOMATION_STATUS: Record<string, { label: string; variant: "success" | "warning" | "muted" | "default"; help: string }> = {
  draft: { label: "draft", variant: "muted", help: "Not running; being built." },
  shadow: { label: "shadow", variant: "default", help: "Runs as a simulation: logs what it would do, sends and changes nothing (to compare with Keap)." },
  active: { label: "live", variant: "success", help: "Running for real: emails go out, tags change." },
  paused: { label: "paused", variant: "warning", help: "Nobody new starts and nobody moves until it's resumed." },
  archived: { label: "archived", variant: "muted", help: "Ended." },
};

/** Keap campaigns rebuilt as automations (crm-automations), with how many people are in each now. */
export function AutomationsPage() {
  const [status, setStatus] = useState("");
  const [brand, setBrand] = useState<number | "">("");
  const q = useQuery({
    queryKey: ["automations-list", status, brand],
    queryFn: () => api<{ automations: AutomationSummary[] }>("/api/v1/automations" + qs({ status: status || undefined, brand_id: brand || undefined, include_archived: status === "archived" || undefined, limit: 500 })),
  });
  const rows = q.data?.automations ?? [];
  return (
    <div className="mx-auto max-w-[1200px]">
      <PageHeader
        title="Automations"
        sub="Keap campaigns rebuilt here"
        actions={
          <>
            <Button size="sm" variant="outline" asChild>
              <Link to="/automations/keap">Keap campaigns rebuilt</Link>
            </Button>
            <Select className="w-auto" value={brand} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")} aria-label="Brand">
              <option value="">All brands</option>
              {BRANDS.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.short}
                </option>
              ))}
            </Select>
            <Select className="w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
              <option value="">Any status</option>
              {Object.entries(AUTOMATION_STATUS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </Select>
          </>
        }
      >
        <p className="text-sm text-[var(--color-muted-foreground)]">
          New automations run in shadow mode first (they only log what they would do), so they can be compared with what Keap actually sent before going live. People
          already partway through a Keap campaign finish in Keap.
        </p>
      </PageHeader>
      <Card className="overflow-x-auto">
        {q.isLoading && <Skeleton className="m-3 h-32" />}
        {!q.isLoading && rows.length === 0 && <Empty title="No automations here yet.">Claude builds them from the Keap campaign snapshot; they show up here as drafts.</Empty>}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH>Automation</TH>
                <TH>Brand</TH>
                <TH>Status</TH>
                <TH className="text-right">In it now</TH>
                <TH className="text-right">Ever started</TH>
                <TH>Changed</TH>
              </tr>
            </THead>
            <tbody>
              {rows.map((a) => (
                <TR key={a.id}>
                  <TD className="max-w-[22rem]">
                    <Link to="/automations/$automationId" params={{ automationId: String(a.id) }} className="block truncate font-medium text-[var(--color-primary)] hover:underline">
                      {a.name}
                    </Link>
                    {a.description && <div className="truncate text-xs text-[var(--color-muted-foreground)]">{a.description}</div>}
                  </TD>
                  <TD>{a.brand_id ? <BrandMark id={a.brand_id} /> : "—"}</TD>
                  <TD>
                    <Badge variant={AUTOMATION_STATUS[a.status]?.variant ?? "muted"} title={AUTOMATION_STATUS[a.status]?.help}>
                      {AUTOMATION_STATUS[a.status]?.label ?? a.status}
                    </Badge>
                  </TD>
                  <TD className="num text-right">{formatInt(a.live_count)}</TD>
                  <TD className="num text-right">{formatInt(a.total_count)}</TD>
                  <TD className="whitespace-nowrap text-xs">{formatAgo(a.updated_at)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
